import React, { useEffect, useRef, useState } from 'react';
import { IFieldOptions, IGeneratePDFState } from '../..';
import css from './index.module.scss';
import { IconNode } from '@components/base';
import Images from '@assets/images';
import { SelectLabel } from '@components/common';
import { Controller, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { Popover } from '@mui/material';

interface ICustomisePDFProps {
  pdfState: IGeneratePDFState;
  setPdfState: React.Dispatch<React.SetStateAction<IGeneratePDFState>>;
  getTableAdhocOptions: () => Promise<void>;
}

interface FieldCardProps {
  index: number;
  option: IFieldOptions;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  onDelete?: (index: number) => void;
  isFixed?: boolean;
}

const CustomisePDF = (props: ICustomisePDFProps) => {
  const { control, setValue } = useFormContext();
  const { pdfState, getTableAdhocOptions } = props;
  const { tableOptions, adhocOptions, fixedTableOptions } = pdfState;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);

  const selectedTableOptionsWatch = useWatch({
    name: 'tableFields',
    control
  });

  const onTableDataChange = (data: IFieldOptions[] | null) => {
    if (!data) {
      setValue('tableFields', fixedTableOptions || []);
      return;
    }
    const fixedOptionsToAdd =
      fixedTableOptions?.filter(
        (fixedOpt) => !data.some((selectedOpt) => selectedOpt.key === fixedOpt.key)
      ) || [];
    setValue('tableFields', [...fixedOptionsToAdd, ...data]);
  };

  // Check if an option is a fixed option
  const isFixedOption = (option: IFieldOptions): boolean => {
    return fixedTableOptions?.some((fixed) => fixed.key === option.key) || false;
  };

  //Reorder the array when one card is dropped onto another
  const moveCard = (dragIndex: number, hoverIndex: number) => {
    const updated = [...selectedTableOptionsWatch];
    const [removed] = updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, removed);
    setValue('tableFields', updated);
  };

  const onDelete = (index: number) => {
    const optionToDelete = selectedTableOptionsWatch[index];
    if (isFixedOption(optionToDelete)) {
      return;
    }
    const updated = [...selectedTableOptionsWatch];
    updated.splice(index, 1);
    setValue('tableFields', updated);
  };

  const handlePopoverOpen = (event: React.SyntheticEvent) => {
    const target = event.currentTarget as HTMLButtonElement;
    setAnchorEl(target);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const adhocFieldsLabelJSX = (
    <div className={css.labelWithIcon}>
      <span>Adhoc Fields</span>
      <IconNode
        src={Images.infoGrey}
        alt="Info icon"
        onClick={handlePopoverOpen}
        className={css.infoIcon}
      />
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        className={css.popover}
      >
        <div className={css.popoverContent}>
          <img
            src={Images.quoteAdhocScreenshot}
            alt="Adhoc fields example"
            className={css.popoverImage}
          />
        </div>
      </Popover>
    </div>
  );

  useEffect(() => {
    getTableAdhocOptions?.();
  }, []);

  return (
    <main className={css.mainWrapper}>
      <section className={css.adhocWrapper}>
        <>
          {adhocFieldsLabelJSX}
          <Controller
            name="adhocFields"
            control={control}
            render={({ field: { value } }) => (
              <SelectLabel
                options={adhocOptions}
                value={value}
                onChange={(data: any) => {
                  setValue('adhocFields', data);
                }}
                isMulti
                isSearchable
                getOptionLabel={(option: any) => option.label}
                getOptionValue={(option: any) => option.key}
                isOptionSelected={(option: any, selectValue) =>
                  selectValue.some((val: any) => val.key === option.key)
                }
                placeholder="Select Adhoc Field(s)"
                rootClassName={css.selectWrapper}
              />
            )}
          />
        </>
      </section>
      <section className={css.tableWrapper}>
        <Controller
          name="tableFields"
          control={control}
          render={({ field: { value } }) => (
            <SelectLabel
              options={tableOptions}
              value={value}
              onChange={(data: any) => {
                onTableDataChange(data);
              }}
              isMulti
              isSearchable
              label="Table Fields"
              getOptionLabel={(option: any) => option.label}
              getOptionValue={(option: any) => option.key}
              isOptionSelected={(option: any, selectValue) =>
                selectValue.some((val: any) => val.key === option.key)
              }
              placeholder="Select Field(s)"
              rootClassName={css.selectWrapper}
              isOptionDisabled={(option: any) => isFixedOption(option)}
            />
          )}
        />
        {selectedTableOptionsWatch.map((option: IFieldOptions, index: number) => (
          <FieldCard
            key={`${option.key}-${index}`}
            option={option}
            index={index}
            moveCard={moveCard}
            onDelete={onDelete}
            isFixed={isFixedOption(option)}
          />
        ))}
      </section>
    </main>
  );
};

export default CustomisePDF;

const FieldCard = ({ option, index, moveCard, onDelete, isFixed = false }: FieldCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Mark yourself as draggable
  const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    // Put which card is being dragged in dataTransfer
    e.dataTransfer.setData('startIndex', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  // We must prevent default for the drop event to be allowed
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const onDragLeave = () => {
    setIsDragOver(false);
  };

  // When dropped, reorder via moveCard
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startIndex = Number(e.dataTransfer.getData('startIndex'));
    moveCard(startIndex, index);
    setIsDragOver(false);
  };

  return (
    <div
      ref={ref}
      className={`${css.cardWrapper} ${isFixed ? css.fixedCard : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className={css.cardTitle}>
        <IconNode
          src={Images.sixDots}
          alt="six dots icon"
          component="button"
          className={css.sixDots}
        />
        <div className={css.title}>{option.label}</div>
      </div>
      <div className={css.closeButton}>
        {!isFixed && (
          <IconNode
            src={Images.crossRed}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={() => onDelete?.(index)}
          />
        )}
      </div>
    </div>
  );
};

export { FieldCard };
